import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Text } from 'components/ui';
import Carousel from 'components/Carousel';
import RewardCard from './RewardCard';
import BeautyInsiderCashContent from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/BeautyInsiderCashContent';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderRewardsSummaryEN from './locales/BeautyInsiderRewardsSummary_en_US';
import BeautyInsiderRewardsSummaryFR from './locales/BeautyInsiderRewardsSummary_fr_CA';
import promoUtils from 'utils/Promos';
import rougeUtils from 'utils/rougeExclusive';
import Location from 'utils/Location';
import basketUtils from 'utils/Basket';
import { showRougeRewardsErrorModal } from 'actions/RougeRewardsActions';
import Actions from 'actions/Actions';
import store from 'store/Store';
import { formatExpirationLocalized } from 'utils/BeautyInsider';
import { space } from 'style/config';
import Empty from 'constants/empty';

const BeautyInsiderRewardsSummary = props => {
    const {
        bankRewards,
        availableRrcCoupons,
        biSummary,
        basket,
        appliedPromoCodes = Empty.Array,
        onApplyRougeReward,
        onRemoveRougeReward,
        onShowLearnMore
    } = props;
    const rewardsTotal = bankRewards && bankRewards.rewardsTotal;
    const { isGlobalEnabled } = Sephora.fantasticPlasticConfigurations || {};
    const isUS = localeUtils.isUS();
    const isFrench = localeUtils.isFrench();

    // Handler functions for Rouge Rewards
    const handleApplyRougeReward = (couponCode, denomination) => {
        // Get basket subtotal (basket.subtotal is a currency string like "$50.00")
        const basketSubTotal = basket?.subtotal ? Number(basketUtils.removeCurrency(basket.subtotal)) : 0;

        // Show error modal if basket is empty or has no value (e.g., only gift cards)
        if (basketUtils.isEmpty(basket) || basketSubTotal === 0) {
            store.dispatch(
                showRougeRewardsErrorModal({
                    title: getText('errorTitle'),
                    message: getText('emptyBasketError'),
                    buttonText: getText('okButtonText')
                })
            );

            return;
        }

        // Warn if basket value less than reward amount, but still allow application
        if (basketSubTotal < denomination) {
            const remaining = denomination - basketSubTotal;
            const formattedRemaining = isFrench ? `${remaining.toFixed(2)} $` : `$${remaining.toFixed(2)}`;

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('rougeRewardsTitle'),
                    message: getText('insufficientBasketWarning', [formattedRemaining]),
                    buttonText: getText('okButtonText'),
                    callback: () => {
                        if (onApplyRougeReward) {
                            onApplyRougeReward(couponCode.toLowerCase());
                        }
                    }
                })
            );

            return;
        }

        if (onApplyRougeReward) {
            onApplyRougeReward(couponCode.toLowerCase());
        }
    };

    const handleRemoveRougeReward = couponCode => {
        if (onRemoveRougeReward) {
            onRemoveRougeReward(couponCode.toLowerCase());
        }
    };

    const handleLearnMore = coupon => {
        if (onShowLearnMore) {
            onShowLearnMore(coupon);
        }
    };

    // Calculate Rouge exclusive badges
    const rougeExclusiveBadgeText = rougeUtils.isRougeExclusive() ? getText('rougeBadge') : '';
    const isElegibleBiCashOptionExclusive = promoUtils.isElegibleBiCashOptionExclusive(biSummary.biCashOptions);
    const rougeBiCashExclusiveText = isElegibleBiCashOptionExclusive && rougeUtils.isRougeCashFlagEnabled ? getText('rougeBadge') : '';
    const promotions = biSummary?.biCashOptions?.availablePromotions || [];

    const rewardCards = [];

    // Credit Card Rewards
    const creditCardCard = getCreditCardRewardsCard(rewardsTotal, isGlobalEnabled, isUS);

    if (creditCardCard) {
        rewardCards.push(creditCardCard);
    }

    // Rouge Rewards
    const rougeCard = getRougeRewardsCard(
        availableRrcCoupons,
        appliedPromoCodes,
        isFrench,
        rougeExclusiveBadgeText,
        handleApplyRougeReward,
        handleRemoveRougeReward,
        handleLearnMore
    );

    if (rougeCard) {
        rewardCards.push(rougeCard);
    }

    // Beauty Insider Cash
    const biCashCard = getBeautyInsiderCashCard(promotions, biSummary, rougeBiCashExclusiveText);

    if (biCashCard) {
        rewardCards.push(biCashCard);
    }

    if (rewardCards.length === 0) {
        return null;
    }

    // Calculate itemWidth based on number of cards
    // Mobile: 315px for multiple items, 100% for single item
    // Large screens: 1/3 width accounting for gaps (gap=3 = 12px)
    const itemWidth =
        rewardCards.length === 1
            ? [`calc(100% - ${space[4]}px)`, `calc((100% - ${space[6] + space[5]}px) / 3)`]
            : [defaultCardWidth, `calc((100% - ${space[6] + space[5]}px) / 3)`];

    return (
        <>
            <Text
                is='h3'
                fontWeight='bold'
                fontSize='lg'
                marginBottom={4}
            >
                {getText('title')}
            </Text>
            <Carousel
                items={rewardCards}
                gap={3}
                size='small'
                paddingY={4}
                marginX={'-container'}
                scrollPadding={[2, 'container']}
                showArrowOnHover={true}
                arrowVariant='circle'
                itemWidth={itemWidth}
            />
        </>
    );
};

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderRewardsSummaryEN, BeautyInsiderRewardsSummaryFR);
const defaultCardWidth = 315;

/**
 * Creates Credit Card Rewards card component
 * @param {number} rewardsTotal - Total credit card rewards amount
 * @param {boolean} isGlobalEnabled - Whether fantastic plastic is globally enabled
 * @param {boolean} isUS - Whether user is in US locale
 * @returns {React.Element|null} RewardCard component or null
 */
const getCreditCardRewardsCard = (rewardsTotal, isGlobalEnabled, isUS) => {
    if (!rewardsTotal || !isGlobalEnabled || !isUS) {
        return null;
    }

    return (
        <RewardCard
            key='credit-card-rewards'
            title={getText('creditCardTitle')}
            primaryText={getText('creditCardPrimaryText', [rewardsTotal])}
            secondaryText={getText('creditCardSecondaryText')}
            buttonText={getText('creditCardButtonText')}
            linkText={getText('creditCardLinkText')}
            linkHref='/creditcard'
            onButtonClick={event => Location.navigateTo(event, '/basket')}
            onLinkClick={event => Location.navigateTo(event, '/creditcard')}
        />
    );
};

/**
 * Creates Rouge Rewards card component
 * @param {Array} availableRrcCoupons - Array of available Rouge Rewards coupons
 * @param {Array} appliedPromoCodes - Array of applied promo codes (lowercase)
 * @param {boolean} isFrench - Whether user is in French locale
 * @param {string} rougeExclusiveBadgeText - Badge text for Rouge exclusive
 * @param {Function} handleApplyRougeReward - Handler for applying Rouge Reward
 * @param {Function} handleRemoveRougeReward - Handler for removing Rouge Reward
 * @param {Function} handleLearnMore - Handler for learn more action
 * @returns {React.Element|null} RewardCard component or null
 */
const getRougeRewardsCard = (
    availableRrcCoupons,
    appliedPromoCodes,
    isFrench,
    rougeExclusiveBadgeText,
    handleApplyRougeReward,
    handleRemoveRougeReward,
    handleLearnMore
) => {
    if (!availableRrcCoupons || availableRrcCoupons.length === 0) {
        return null;
    }

    // Sort coupons: soonest to expire first, then highest denomination
    const sortedCoupons = availableRrcCoupons.slice().sort((a, b) => {
        const dateA = new Date(a.expirationDate);
        const dateB = new Date(b.expirationDate);

        if (dateA - dateB !== 0) {
            return dateA - dateB;
        }

        return b.denomination - a.denomination;
    });

    // Show applied coupon if one exists, otherwise show first sorted coupon
    const primaryCoupon = availableRrcCoupons.find(coupon =>
        appliedPromoCodes.includes(coupon.couponCode.toLowerCase())
    ) || sortedCoupons[0];

    const isApplied = appliedPromoCodes.includes(primaryCoupon.couponCode.toLowerCase());
    const formattedAmount = !isFrench ? `$${primaryCoupon.denomination}` : `${primaryCoupon.denomination} $`;
    const formattedExpiration = formatExpirationLocalized(primaryCoupon.expirationDate, isFrench);

    const secondaryText = formattedExpiration
        ? getText('rougeRewardsExpiresText', [formattedExpiration])
        : getText('rougeRewardsSecondaryText');

    return (
        <RewardCard
            key='rouge-rewards'
            title={getText('rougeRewardsTitle')}
            primaryText={getText('rougeRewardsPrimaryText', [formattedAmount])}
            secondaryText={secondaryText}
            buttonText={getText('rougeRewardsButtonText')}
            linkText={getText('rougeRewardsLinkText')}
            showRougeBadge={!!rougeExclusiveBadgeText}
            applied={isApplied}
            disabled={false}
            onButtonClick={() => handleApplyRougeReward(primaryCoupon.couponCode, primaryCoupon.denomination)}
            onRemoveClick={() => handleRemoveRougeReward(primaryCoupon.couponCode)}
            onLinkClick={() => handleLearnMore(primaryCoupon)}
        />
    );
};

/**
 * Creates Beauty Insider Cash card component
 * @param {Array} promotions - Array of available promotions
 * @param {Object} biSummary - Beauty Insider summary data
 * @param {string} rougeBiCashExclusiveText - Badge text for Rouge BI Cash exclusive
 * @returns {React.Element|null} RewardCard component or null
 */
const getBeautyInsiderCashCard = (promotions, biSummary, rougeBiCashExclusiveText) => {
    if (!promotions.length || !biSummary?.biCashBackAvailabilityMessage) {
        return null;
    }

    // Determine if BI Cash is applied
    // Hardcoded this has to be false for now to show the applied state in the UI, but eventually this should be determined based on the promotions data and user's applied promotions
    const isBiCashApplied = true;
    const displayedPromotion = getDisplayedPromotion(promotions);
    const biCashButtonText = getBiCashButtonText(isBiCashApplied, promotions.length);
    const shouldShowLearnMore = displayedPromotion?.value !== '$5';

    const leftContent = (
        <BeautyInsiderCashContent
            promotions={promotions}
            displayedPromotion={displayedPromotion}
            title={getText('biCashTitle')}
            applied={isBiCashApplied}
        />
    );

    return (
        <RewardCard
            key='beauty-insider-cash'
            title={getText('biCashTitle')}
            secondaryText={getText('biCashSecondaryText')}
            buttonText={biCashButtonText}
            linkText={shouldShowLearnMore ? getText('biCashLinkText') : null}
            showRougeBadge={!!rougeBiCashExclusiveText}
            onButtonClick={() => {}}
            onLinkClick={() => {}}
            leftContent={leftContent}
            applied={isBiCashApplied}
        />
    );
};

/**
 * Determines the button text for Beauty Insider Cash based on applied state and promotions
 * @param {boolean} isApplied - Whether BI Cash is already applied
 * @param {number} promotionsCount - Number of available promotions
 * @returns {string} The appropriate button text
 */
const getBiCashButtonText = (isApplied, promotionsCount) => {
    if (isApplied) {
        return getText('biCashButtonTextApplied');
    }

    if (promotionsCount === 1) {
        return getText('biCashButtonText'); // "Apply"
    }

    if (promotionsCount >= 2) {
        return getText('biCashButtonTextChoose'); // "Choose"
    }

    return getText('biCashButtonText'); // Default to "Apply"
};

/**
 * Gets the displayed promotion object (for single or 3+ promotions)
 * @param {Array} promotions - Array of promotion objects
 * @returns {Object|null} The promotion to display or null
 */
const getDisplayedPromotion = promotions => {
    if (!promotions || promotions.length === 0) {
        return null;
    }

    // 1 promotion: return that promotion
    if (promotions.length === 1) {
        return promotions[0];
    }

    // 2 promotions: return null (different UI for 2 promotions)
    if (promotions.length === 2) {
        return null;
    }

    // 3+ promotions: return highest value promotion
    const sortedPromos = promotions.slice().sort((a, b) => {
        const valueA = parseInt(a.value?.replace('$', '') || '0', 10);
        const valueB = parseInt(b.value?.replace('$', '') || '0', 10);

        return valueB - valueA;
    });

    return sortedPromos[0];
};

export default wrapFunctionalComponent(BeautyInsiderRewardsSummary, 'BeautyInsiderRewardsSummary');
